import React, { useState, useEffect, useMemo, useRef } from 'react';
import Select from 'react-select';
import { calculatePlayerPerformanceScore } from '../utils/playerUtils';
import { getTeamLogo } from '../utils/teamUtils';
import { ChevronDownIcon } from '@heroicons/react/24/outline';

const HitRates = ({ players, fixtures, isDarkMode }) => {
  // Update getDefaultRound to use fixtures like in Sidebar.js
  const getDefaultRound = () => {
    if (!fixtures || fixtures.length === 0) return 'Round 1';
    
    const today = new Date();
    
    // Find the first fixture that hasn't been played yet
    const upcomingFixture = fixtures.find(fixture => {
      if (!fixture.Date) return false;
      
      const [datePart, timePart] = fixture.Date.split(' ');
      const [day, month, year] = datePart.split('/');
      const [hours, minutes] = timePart.split(':');
      
      const fixtureDate = new Date(year, month - 1, day, hours, minutes);
      return fixtureDate >= today;
    });
    
    if (upcomingFixture) {
      return `Round ${upcomingFixture['Round Number']}`;
    }
    
    // If no upcoming fixtures, get the last regular round
    const lastRegularRound = fixtures.reduce((lastRound, fixture) => {
      const roundNum = fixture['Round Number'];
      if (
        roundNum && 
        !isNaN(parseInt(roundNum)) && 
        !roundNum.includes('Finals') && 
        !roundNum.includes('Grand')
      ) {
        return Math.max(lastRound, parseInt(roundNum));
      }
      return lastRound;
    }, 1);
    
    return `Round ${lastRegularRound}`;
  };

  // State variables
  const [selectedStat, setSelectedStat] = useState('all_run_metres');
  const [selectedTeams, setSelectedTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hitRateData, setHitRateData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [activeSortColumn, setActiveSortColumn] = useState('hitRate');
  const [sortDirection, setSortDirection] = useState('desc');
  const [matchups, setMatchups] = useState([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [round, setRound] = useState(getDefaultRound);
  const [selectedYears, setSelectedYears] = useState(['2025', '2024']);
  const [showRoundPicker, setShowRoundPicker] = useState(false);
  const roundPickerRef = useRef(null);
  
  // Define stats options with default values/lines
  const statTabs = [
    { id: 'tries', label: 'Tries', defaultLine: 1 },
    { id: 'performance_points', label: 'Player Performance Points', defaultLine: 40 },
    { id: 'points', label: 'Points', defaultLine: 6 },
    { id: 'all_run_metres', label: 'Run Metres', defaultLine: 100 },
    { id: 'tackles_made', label: 'Tackles', defaultLine: 20 },
    { id: 'line_breaks', label: 'Line Breaks', defaultLine: 1 },
    { id: 'tackle_breaks', label: 'Tackle Breaks', defaultLine: 3 },
    { id: 'kick_metres', label: 'Kick Metres', defaultLine: 200 },
    { id: 'offloads', label: 'Offloads', defaultLine: 1 },
    { id: 'try_assists', label: 'Try Assists', defaultLine: 1 },
  ];

  // All NRL teams
  const teams = [
    'Broncos', 'Bulldogs', 'Cowboys', 'Dolphins', 'Dragons', 'Eels',
    'Knights', 'Panthers', 'Raiders', 'Rabbitohs', 'Roosters', 'Sea Eagles',
    'Sharks', 'Storm', 'Tigers', 'Warriors'
  ];

  // Create team options for the multi-select
  const teamOptions = teams.map(team => ({
    value: team,
    label: team
  }));

  // State for the line (threshold) for selected stat
  const [line, setLine] = useState(statTabs[0].defaultLine);

  // Update line when selected stat changes
  useEffect(() => {
    const selectedStatObj = statTabs.find(opt => opt.id === selectedStat);
    if (selectedStatObj) {
      setLine(selectedStatObj.defaultLine);
    }
  }, [selectedStat]);

  // Calculate hit rate data when filters change
  useEffect(() => {
    if (players && players.length > 0) {
      calculateHitRates();
    }
  }, [players, selectedStat, line, selectedYears, selectedTeams]);

  // Update the matchups useEffect
  useEffect(() => {
    const getMatchups = () => {
      // Extract round number without the "Round " prefix
      const currentRoundNumber = round.replace('Round ', '');
      
      // Filter fixtures directly by the round number from fixtures
      const roundFixtures = fixtures.filter(fixture => 
        fixture['Round Number'] === currentRoundNumber
      );
      
      const matchupsList = roundFixtures.map(fixture => ({
        homeTeam: fixture['Home Team'],
        awayTeam: fixture['Away Team'],
        date: fixture['Date'],
        location: fixture['Location']
      }));
      
      setMatchups(matchupsList);
    };
    
    if (fixtures && fixtures.length > 0) {
      getMatchups();
    }
  }, [fixtures, round]);

  // Add this useEffect for handling clicks outside the dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.round-dropdown')) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Add this useEffect to handle clicks outside the round picker
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (roundPickerRef.current && !roundPickerRef.current.contains(event.target)) {
        setShowRoundPicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const calculateHitRates = () => {
    setLoading(true);
    
    try {
      const filteredPlayers = players.filter(p => selectedYears.includes(String(p.year)));
      const uniquePlayerNames = [...new Set(filteredPlayers.map(p => p.player_name))];
      
      const hitRates = uniquePlayerNames.map(playerName => {
        const playerGames = filteredPlayers.filter(p => p.player_name === playerName)
          .sort((a, b) => {
            if (a.year !== b.year) return b.year - a.year;
            return parseInt(b.round.replace('Round ', '')) - parseInt(a.round.replace('Round ', ''));
          });
        
        if (playerGames.length === 0) return null;
        
        const playerTeam = playerGames[0].player_team;
        
        if (selectedTeams.length > 0 && !selectedTeams.includes(playerTeam)) return null;
        
        // Calculate values based on selected stat
        const getStatValue = (game) => {
          if (selectedStat === 'performance_points') {
            return calculatePlayerPerformanceScore(game);
          }
          return parseFloat(game[selectedStat]) || 0;
        };
        
        const totalGames = playerGames.length;
        const hitGames = playerGames.filter(game => {
          const statValue = getStatValue(game);
          return statValue >= line;
        }).length;
        
        const hitRate = totalGames > 0 ? (hitGames / totalGames) * 100 : 0;
        
        // Calculate average using the same getStatValue function
        const avgValue = playerGames.reduce((sum, game) => {
          return sum + getStatValue(game);
        }, 0) / totalGames;
        
        // Format round values using the same function
        const roundValues = playerGames.map(game => ({
          year: game.year,
          round: game.round,
          value: getStatValue(game),
          opponent: game.is_home_game ? game.away_team : game.home_team
        }));
        
        return {
          id: `${playerName}_${playerTeam}`,
          playerName,
          team: playerTeam,
          games: totalGames,
          hits: hitGames,
          hitRate: Math.round(hitRate).toString(),
          avg: avgValue.toFixed(1),
          roundValues
        };
      }).filter(Boolean);
      
      setHitRateData(hitRates);
    } catch (error) {
      console.error('Error calculating hit rates:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle sorting
  const handleSort = (column) => {
    if (activeSortColumn === column) {
      // Toggle direction if same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new column and default to descending
      setActiveSortColumn(column);
      setSortDirection('desc');
    }
  };

  // Get sorted and paginated data
  const displayData = useMemo(() => {
    // Sort data
    const sorted = [...hitRateData].sort((a, b) => {
      const aValue = activeSortColumn === 'playerName' ? a[activeSortColumn] : parseFloat(a[activeSortColumn]);
      const bValue = activeSortColumn === 'playerName' ? b[activeSortColumn] : parseFloat(b[activeSortColumn]);
      
      if (sortDirection === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
    
    // Paginate
    const startIndex = (currentPage - 1) * pageSize;
    return sorted.slice(startIndex, startIndex + pageSize);
  }, [hitRateData, activeSortColumn, sortDirection, currentPage, pageSize]);

  // Calculate total pages
  const totalPages = Math.ceil(hitRateData.length / pageSize);

  // Get color class for hit rate percentage
  const getHitRateColorClass = (percentage) => {
    const value = parseFloat(percentage);
    if (value >= 75) return 'bg-green-500 text-white';
    if (value >= 50) return 'bg-lime-500 text-white';
    if (value >= 33) return 'bg-yellow-500';
    return 'bg-red-500 text-white';
  };

  // Add this helper function
  const formatMatchupDate = (date) => {
    if (!date) return '';
    const [datePart, timePart] = date.split(' ');
    const [day, month, year] = datePart.split('/');
    const months = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${day} ${months[parseInt(month)]} ${timePart}`;
  };

  // Update getRounds to use fixtures directly
  const getRounds = () => {
    if (!fixtures || fixtures.length === 0) return [];
    
    // Get unique round numbers from fixtures and format them
    return [...new Set(fixtures.map(f => f['Round Number']))]
      .filter(roundNum => !isNaN(parseInt(roundNum)))
      .sort((a, b) => parseInt(a) - parseInt(b))
      .map(roundNum => `Round ${roundNum}`);
  };

  // Update the years array
  const years = ['2025', '2024', '2023', '2022', '2021', '2020'];

  // Update the yearOptions
  const yearOptions = years.map(year => ({
    value: year,
    label: year
  }));

  // Add this helper function to get unique rounds from all player data
  const getUniqueRounds = (data) => {
    const rounds = new Set();
    data.forEach(player => {
      player.roundValues.forEach(rv => {
        rounds.add(`${rv.year} ${rv.round}`);
      });
    });
    return Array.from(rounds).sort((a, b) => {
      const [yearA, roundA] = a.split(' Round ');
      const [yearB, roundB] = b.split(' Round ');
      // Sort by year descending first
      if (yearA !== yearB) return parseInt(yearB) - parseInt(yearA);
      // Then by round descending
      return parseInt(roundB) - parseInt(roundA);
    });
  };

  const adjustLine = (amount) => {
    // Stats that should only change by 1
    const singleIncrementStats = ['tries', 'try_assists', 'points', 'offloads', 'line_breaks'];
    
    const increment = singleIncrementStats.includes(selectedStat) ? 
      Math.sign(amount) : // Will return 1 or -1
      amount;
    
    setLine(prev => Math.max(1, prev + increment));
  };

  const toggleYear = (year) => {
    if (selectedYears.includes(year)) {
      setSelectedYears(selectedYears.filter(y => y !== year));
    } else {
      setSelectedYears([...selectedYears, year]);
    }
  };

  return (
    <div className="w-full px-4 py-4">
      <div className="mb-2">
        <h2 className="text-xl md:text-2xl font-bold mb-1 dark:text-white">Hit Rates</h2>
        <p className="text-xs md:text-sm text-gray-600 dark:text-gray-300 mb-2">
          Player hit rates for different statistical thresholds. Shows the percentage of games a player has hit a given statistical mark.
        </p>
        
        {/* Tabs and filters in a more compact layout */}
        <div className="flex flex-col gap-2">
          {/* First row: Stats tabs in scrollable container */}
          <div className="overflow-x-auto">
            <div className="flex gap-1 md:gap-2 min-w-max pb-1">
              {statTabs.map(stat => (
                <button
                  key={stat.id}
                  onClick={() => {
                    setSelectedStat(stat.id);
                    setLine(stat.defaultLine);
                  }}
                  className={`px-2 py-1 md:px-3 md:py-1.5 text-xs md:text-sm rounded-md transition-colors whitespace-nowrap ${
                    selectedStat === stat.id
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  {stat.label}
                </button>
              ))}
            </div>
          </div>
          
          {/* Second row: Round selector and matchups - more compact */}
          <div className="flex items-center gap-1.5 md:gap-3">
            {/* Round Selector - More compact */}
            <div className="relative inline-block round-dropdown">
              <button 
                className="px-2 py-1 md:px-3 md:py-1.5 text-xs md:text-sm rounded-md bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600 flex items-center"
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              >
                <span>{round}</span>
                <ChevronDownIcon className="w-3 h-3 md:w-4 md:h-4 ml-1" />
              </button>
              {isDropdownOpen && (
                <div className="absolute z-50 mt-1 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg overflow-hidden">
                  <div className="max-h-60 overflow-y-auto">
                    {getRounds().map(r => (
                      <button
                        key={r}
                        className={`w-full text-left px-3 py-1.5 text-xs md:text-sm ${
                          round === r
                            ? 'bg-accent text-white dark:bg-accent-light dark:text-gray-900'
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                        }`}
                        onClick={() => {
                          setRound(r);
                          setIsDropdownOpen(false);
                        }}
                      >
                        {r}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Matchup Buttons - Compact and scrollable */}
            <div className="flex-1 overflow-x-auto">
              <div className="flex gap-1.5 min-w-max pb-1">
                {matchups.map((matchup, index) => (
                  <button 
                    key={index}
                    onClick={() => {
                      const teams = [matchup.homeTeam, matchup.awayTeam];
                      setSelectedTeams(prevTeams => {
                        const allSelected = teams.every(team => prevTeams.includes(team));
                        if (allSelected) {
                          return prevTeams.filter(team => !teams.includes(team));
                        } else {
                          return [...new Set([...prevTeams, ...teams])];
                        }
                      });
                    }}
                    className={`flex-shrink-0 flex items-center bg-gray-100 dark:bg-gray-700 rounded-md px-1.5 py-0.5 md:px-2 md:py-1 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors ${
                      selectedTeams.includes(matchup.homeTeam) && selectedTeams.includes(matchup.awayTeam)
                        ? 'ring-1 ring-blue-500'
                        : ''
                    }`}
                  >
                    <img src={getTeamLogo(matchup.homeTeam)} alt={matchup.homeTeam} className="h-4 w-4 md:h-5 md:w-5" />
                    <span className="mx-0.5 text-xs md:text-sm text-gray-600 dark:text-gray-300">vs</span>
                    <img src={getTeamLogo(matchup.awayTeam)} alt={matchup.awayTeam} className="h-4 w-4 md:h-5 md:w-5" />
                  </button>
                ))}
              </div>
            </div>
          </div>
          
          {/* Third row: Line, years and page size in a single row */}
          <div className="flex flex-wrap items-center gap-2 text-xs md:text-sm">
            {/* Line input - More compact */}
            <div className="flex items-center">
              <span className="text-xs md:text-sm text-gray-600 dark:text-gray-300 mr-1">Line:</span>
              <div className="flex items-center border rounded-md overflow-hidden h-7 md:h-8">
                <button 
                  onClick={() => adjustLine(-5)}
                  className="px-2 py-0.5 md:px-3 md:py-1 text-xs md:text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
                >
                  -
                </button>
                <input
                  type="number"
                  value={line}
                  onChange={(e) => setLine(parseInt(e.target.value) || 0)}
                  className="w-12 md:w-16 px-1 py-0.5 md:px-2 md:py-1 text-xs md:text-sm bg-white dark:bg-gray-800 border-x border-gray-200 dark:border-gray-600 text-center"
                />
                <button 
                  onClick={() => adjustLine(5)}
                  className="px-2 py-0.5 md:px-3 md:py-1 text-xs md:text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
                >
                  +
                </button>
              </div>
            </div>

            {/* Year buttons - Compact row */}
            <div className="flex items-center">
              <span className="text-xs md:text-sm text-gray-600 dark:text-gray-300 mr-1">Years:</span>
              <div className="flex gap-1 md:gap-2">
                {years.map(y => (
                  <button
                    key={y}
                    onClick={() => toggleYear(y)}
                    className={`px-1.5 py-0.5 md:px-2 md:py-1 text-xs md:text-sm rounded-md ${
                      selectedYears.includes(y)
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    {y}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      ) : (
        <>
          {/* Table as the main focal point - with optimized mobile sizing */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow mt-1">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  <tr>
                    {/* Team column */}
                    <th className="px-1 py-2 md:px-2 md:py-3 text-center text-[10px] md:text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider sticky left-0 bg-gray-50 dark:bg-gray-800 z-10 w-[40px] md:w-[50px] min-w-[40px] md:min-w-[50px] max-w-[40px] md:max-w-[50px]">
                      Team
                    </th>
                    
                    {/* Player column */}
                    <th 
                      className={`px-2 py-2 md:px-3 md:py-3 text-left text-[10px] md:text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer sticky left-[40px] md:left-[50px] bg-gray-50 dark:bg-gray-800 z-10 w-[140px] md:w-[180px] min-w-[140px] md:min-w-[180px] max-w-[140px] md:max-w-[180px] ${
                        activeSortColumn === 'playerName' 
                          ? 'bg-gray-100 dark:bg-gray-700' 
                          : ''
                      }`}
                      onClick={() => handleSort('playerName')}
                    >
                      <div className="flex items-center">
                        <span className="flex-1">Player</span>
                        <span className="flex-none w-4">
                          {activeSortColumn === 'playerName' ? (sortDirection === 'asc' ? '↑' : '↓') : ''}
                        </span>
                      </div>
                    </th>
                    
                    {/* Games column */}
                    <th className="px-1 py-2 md:px-2 md:py-3 text-center text-[10px] md:text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider sticky left-[180px] md:left-[230px] bg-gray-50 dark:bg-gray-800 z-10 w-[30px] md:w-[40px] min-w-[30px] md:min-w-[40px] max-w-[30px] md:max-w-[40px]">
                      G
                    </th>
                    
                    {/* Average column */}
                    <th 
                      className={`px-1 py-2 md:px-2 md:py-3 text-center text-[10px] md:text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer sticky left-[210px] md:left-[270px] bg-gray-50 dark:bg-gray-800 z-10 w-[50px] md:w-[60px] min-w-[50px] md:min-w-[60px] max-w-[50px] md:max-w-[60px] ${
                        activeSortColumn === 'avg' 
                          ? 'bg-gray-100 dark:bg-gray-700' 
                          : ''
                      }`}
                      onClick={() => handleSort('avg')}
                    >
                      <div className="flex items-center justify-center">
                        <span className="flex-1">Avg</span>
                        <span className="flex-none w-4">
                          {activeSortColumn === 'avg' ? (sortDirection === 'asc' ? '↑' : '↓') : ''}
                        </span>
                      </div>
                    </th>
                    
                    {/* Hit Rate column */}
                    <th 
                      className={`px-1 py-2 md:px-2 md:py-3 text-center text-[10px] md:text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer sticky left-[260px] md:left-[330px] bg-gray-50 dark:bg-gray-800 z-10 w-[55px] md:w-[70px] min-w-[55px] md:min-w-[70px] max-w-[55px] md:max-w-[70px] ${
                        activeSortColumn === 'hitRate' 
                          ? 'bg-gray-100 dark:bg-gray-700' 
                          : ''
                      }`}
                      onClick={() => handleSort('hitRate')}
                    >
                      <div className="flex items-center justify-center">
                        <span className="flex-1">{line}+</span>
                        <span className="flex-none w-4">
                          {activeSortColumn === 'hitRate' ? (sortDirection === 'asc' ? '↑' : '↓') : ''}
                        </span>
                      </div>
                    </th>
                    
                    {/* Shadow border for scrollable content */}
                    <th className="px-1 py-2 md:px-2 md:py-3 text-center text-[10px] md:text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider w-[50px] md:w-[60px] min-w-[50px] md:min-w-[60px] bg-gray-50 dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]">
                      {getUniqueRounds(hitRateData)[0]?.replace('Round ', 'R')}
                    </th>
                    
                    {/* Round columns */}
                    {getUniqueRounds(hitRateData).slice(1).map(round => (
                      <th key={round} className="px-1 py-2 md:px-2 md:py-3 text-center text-[10px] md:text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider w-[50px] md:w-[60px] min-w-[50px] md:min-w-[60px] bg-gray-50 dark:bg-gray-800">
                        {round.replace('Round ', 'R')}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {displayData.map(player => (
                    <tr key={player.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-1 py-2 md:px-2 md:py-3 whitespace-nowrap text-center sticky left-0 bg-white dark:bg-gray-800 z-10 w-[40px] md:w-[50px] min-w-[40px] md:min-w-[50px] max-w-[40px] md:max-w-[50px]">
                        <img 
                          src={getTeamLogo(player.team)} 
                          alt={player.team} 
                          className="w-5 h-5 md:w-6 md:h-6 inline-block"
                        />
                      </td>
                      <td className="px-2 py-2 md:px-3 md:py-3 whitespace-nowrap text-xs md:text-sm text-gray-700 dark:text-gray-300 sticky left-[40px] md:left-[50px] bg-white dark:bg-gray-800 z-10 w-[140px] md:w-[180px] min-w-[140px] md:min-w-[180px] max-w-[140px] md:max-w-[180px]">
                        {player.playerName}
                      </td>
                      <td className="px-1 py-2 md:px-2 md:py-3 whitespace-nowrap text-xs md:text-sm text-center text-gray-700 dark:text-gray-300 sticky left-[180px] md:left-[230px] bg-white dark:bg-gray-800 z-10 w-[30px] md:w-[40px] min-w-[30px] md:min-w-[40px] max-w-[30px] md:max-w-[40px]">
                        {player.games}
                      </td>
                      <td className="px-1 py-2 md:px-2 md:py-3 whitespace-nowrap text-xs md:text-sm text-center text-gray-700 dark:text-gray-300 sticky left-[210px] md:left-[270px] bg-white dark:bg-gray-800 z-10 w-[50px] md:w-[60px] min-w-[50px] md:min-w-[60px] max-w-[50px] md:max-w-[60px]">
                        {player.avg}
                      </td>
                      <td className="px-1 py-2 md:px-2 md:py-3 whitespace-nowrap text-xs md:text-sm text-center sticky left-[260px] md:left-[330px] bg-white dark:bg-gray-800 z-10 w-[55px] md:w-[70px] min-w-[55px] md:min-w-[70px] max-w-[55px] md:max-w-[70px]">
                        <span className={`inline-block rounded-full px-1.5 py-0.5 md:px-2 md:py-1 text-[10px] md:text-xs font-medium ${getHitRateColorClass(player.hitRate)}`}>
                          {player.hitRate}%
                        </span>
                      </td>

                      {/* Round columns - each round gets its own column */}
                      {getUniqueRounds(hitRateData).map(round => {
                        const roundValue = player.roundValues.find(rv => `${rv.year} ${rv.round}` === round);
                        return (
                          <td key={round} className="px-1 py-2 md:px-2 md:py-3 text-center">
                            {roundValue ? (
                              <div 
                                className={`inline-block rounded-full px-1.5 py-0.5 md:px-2 md:py-1 text-[10px] md:text-xs font-medium ${
                                  roundValue.value >= line ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
                                }`}
                                title={`vs ${roundValue.opponent}`}
                              >
                                {Math.round(roundValue.value)}
                              </div>
                            ) : (
                              <div className="inline-block rounded-full px-1.5 py-0.5 md:px-2 md:py-1 text-[10px] md:text-xs font-medium bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400">
                                -
                              </div>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination - Updated with page size selector */}
          {totalPages > 1 && (
            <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-1 md:gap-2">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="px-2 py-1 md:px-3 md:py-1.5 text-xs md:text-sm rounded-md bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 disabled:opacity-50"
                >
                  Prev
                </button>
                <span className="px-2 py-1 md:px-3 md:py-1.5 text-xs md:text-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-md">
                  {currentPage} / {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="px-2 py-1 md:px-3 md:py-1.5 text-xs md:text-sm rounded-md bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 disabled:opacity-50"
                >
                  Next
                </button>
              </div>

              <div className="flex items-center gap-1 md:gap-2">
                <select
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                  className="px-1 py-1 md:px-2 md:py-1.5 text-xs md:text-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-md"
                >
                  {[15, 25, 50, 100].map(size => (
                    <option key={size} value={size}>{size}</option>
                  ))}
                </select>
                <span className="text-xs md:text-sm text-gray-600 dark:text-gray-300">per page</span>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default HitRates; 